import { apiClient } from './client';

export interface OfferModule {
  id: number;
  code: string;
  name: string;
  isDefault: boolean;
  price: number | null;
  priceT2: number | null;
  priceT3: number | null;
  priceT4: number | null;
}

export interface OfferModuleInput {
  code: string;
  isDefault?: boolean;
  price?: number | null;
  priceT2?: number | null;
  priceT3?: number | null;
  priceT4?: number | null;
}

export interface Offer {
  id: number;
  code: string;
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  moduleCount: number;
  schoolCount: number;
  modules: OfferModule[];
  priceT1: number | null;
  priceT2: number | null;
  priceT3: number | null;
  priceT4: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface OfferDetail extends Offer {
  product: {
    code: string;
    name: string;
  };
}

export interface ProductOption {
  id: number;
  code: string;
  name: string;
  description: string | null;
  type: 'recurring' | 'one_shot';
  price: number | null;
  priceT2: number | null;
  priceT3: number | null;
  priceT4: number | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface Product {
  id: number;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  offerCount: number;
  optionCount: number;
  schoolCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ProductSchool {
  id: number;
  code: string;
  name: string;
  offerCode: string;
  offerName: string;
}

export interface ProductDetail extends Product {
  offers: Offer[];
  options: ProductOption[];
  schools: ProductSchool[];
}

export interface CreateProductData {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateProductData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface CreateOfferData {
  code: string;
  name: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
  priceT1?: number | null;
  priceT2?: number | null;
  priceT3?: number | null;
  priceT4?: number | null;
  modules?: Array<string | OfferModuleInput>;
}

export interface UpdateOfferData {
  name?: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
  priceT1?: number | null;
  priceT2?: number | null;
  priceT3?: number | null;
  priceT4?: number | null;
  modules?: Array<string | OfferModuleInput>;
}

export interface CreateOptionData {
  code: string;
  name: string;
  description?: string;
  type?: 'recurring' | 'one_shot';
  price?: number | null;
  priceT2?: number | null;
  priceT3?: number | null;
  priceT4?: number | null;
  displayOrder?: number;
  isActive?: boolean;
}

export interface UpdateOptionData {
  name?: string;
  description?: string;
  type?: 'recurring' | 'one_shot';
  price?: number | null;
  priceT2?: number | null;
  priceT3?: number | null;
  priceT4?: number | null;
  displayOrder?: number;
  isActive?: boolean;
}

export const productsApi = {
  // Products
  async getAll(activeOnly: boolean = false): Promise<Product[]> {
    const params = activeOnly ? { active: 'true' } : {};
    const response = await apiClient.get<Product[]>('/api/products', { params });
    return response.data;
  },

  async getByCode(code: string): Promise<ProductDetail> {
    const response = await apiClient.get<ProductDetail>(`/api/products/${code}`);
    return response.data;
  },

  async create(data: CreateProductData): Promise<Product> {
    const response = await apiClient.post<Product>('/api/products', data);
    return response.data;
  },

  async update(code: string, data: UpdateProductData): Promise<Product> {
    const response = await apiClient.put<Product>(`/api/products/${code}`, data);
    return response.data;
  },

  async delete(code: string): Promise<void> {
    await apiClient.delete(`/api/products/${code}`);
  },

  // Offers
  async getOffers(productCode: string): Promise<Offer[]> {
    const response = await apiClient.get<Offer[]>(`/api/products/${productCode}/offers`);
    return response.data;
  },

  async getOffer(productCode: string, offerCode: string): Promise<OfferDetail> {
    const response = await apiClient.get<OfferDetail>(`/api/products/${productCode}/offers/${offerCode}`);
    return response.data;
  },

  async createOffer(productCode: string, data: CreateOfferData): Promise<Offer> {
    const response = await apiClient.post<Offer>(`/api/products/${productCode}/offers`, data);
    return response.data;
  },

  async updateOffer(productCode: string, offerCode: string, data: UpdateOfferData): Promise<Offer> {
    const response = await apiClient.put<Offer>(`/api/products/${productCode}/offers/${offerCode}`, data);
    return response.data;
  },

  async deleteOffer(productCode: string, offerCode: string): Promise<void> {
    await apiClient.delete(`/api/products/${productCode}/offers/${offerCode}`);
  },

  // Options
  async getOptions(productCode: string): Promise<ProductOption[]> {
    const response = await apiClient.get<ProductOption[]>(`/api/products/${productCode}/options`);
    return response.data;
  },

  async createOption(productCode: string, data: CreateOptionData): Promise<ProductOption> {
    const response = await apiClient.post<ProductOption>(`/api/products/${productCode}/options`, data);
    return response.data;
  },

  async updateOption(productCode: string, optionId: number, data: UpdateOptionData): Promise<ProductOption> {
    const response = await apiClient.put<ProductOption>(`/api/products/${productCode}/options/${optionId}`, data);
    return response.data;
  },

  async deleteOption(productCode: string, optionId: number): Promise<void> {
    await apiClient.delete(`/api/products/${productCode}/options/${optionId}`);
  },
};
